import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, GraduationCap, ShieldCheck, MapPin, BookOpen, ArrowRight,
} from "lucide-react";
import { api } from "../../lib/api";

/**
 * Compact CRM KPI strip — "CRM Heartbeat".
 *
 * Five horizontally-aligned compact cards (Total Leads / Total Înscrieri /
 * Consent % / Top Județ / Top Specializare) that stay visible on top of
 * every CRM tab so the team has quick context regardless of where they are.
 *
 * M915 — The deeper "Top 5 județe + distribuție surse" drawer that used to
 * live behind a toggle here has been REMOVED because it duplicated the
 * Analytics v1 (M914) tab. A discreet "Vezi Analytics complete →" link is
 * shown next to the strip to drive discovery of the full analytics view.
 *
 * Data still comes from the legacy `GET /api/admin/analytics/summary`
 * endpoint (60s cache). The endpoint is intentionally kept around to avoid
 * breaking existing CRM tests; it can be re-pointed to the M914 `overview`
 * endpoint in a later cleanup pass without UX change.
 */
export default function AnalyticsCards() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/analytics/summary")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5" data-testid="analytics-cards-loading">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-[#E5E7EB] p-3 h-[64px] animate-pulse" />
        ))}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-2" data-testid="analytics-cards">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
        <Card icon={<Users size={14} />}         label="Leaduri"     value={data.total_leads}                                testid="analytics-total-leads" />
        <Card icon={<GraduationCap size={14} />} label="Înscrieri"   value={data.total_enrollments}                          testid="analytics-total-enrollments" />
        <Card icon={<ShieldCheck size={14} />}   label="Consent mkt" value={`${data.marketing_consent_pct}%`} hint={`${data.marketing_consent_yes}/${data.total_leads}`} testid="analytics-consent" />
        <Card icon={<MapPin size={14} />}        label="Top județ"   value={data.top_county || "—"}           hint={data.top_counties?.[0] ? `${data.top_counties[0].count} leads` : null}        testid="analytics-top-county" />
        <Card icon={<BookOpen size={14} />}      label="Top spec."   value={data.top_specializare || "—"}     hint={data.top_specializari?.[0] ? `${data.top_specializari[0].count} leads` : null} testid="analytics-top-spec" />
      </div>
      <div className="flex justify-end">
        <Link
          to="/admin/analytics"
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#965EA7] hover:text-[#7A4986] transition-colors"
          data-testid="analytics-cards-link"
        >
          Vezi Analytics complete <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}

function Card({ icon, label, value, hint, testid }) {
  return (
    <div
      className="bg-white border border-[#E5E7EB] rounded-xl px-3.5 py-2.5 flex items-center gap-2.5"
      data-testid={testid}
    >
      <div className="rounded-lg bg-[#F5F6F6] text-[#373A45] p-1.5 shrink-0" aria-hidden="true">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[#6B7280] leading-tight">
          {label}
        </div>
        <div className="font-display text-lg font-bold text-[#373A45] truncate leading-tight" title={String(value)}>
          {value}
        </div>
        {hint && <div className="text-[10px] text-[#6B7280] mt-0.5 leading-tight">{hint}</div>}
      </div>
    </div>
  );
}
